import type { PostToDealEvidence } from '@/classes/ScrapedPostData'

function uniqueSorted(keys: string[]): string[] {
  return [...new Set(keys)].sort()
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function observationKeys(
  map: unknown,
  predicate: (obs: Record<string, unknown>) => boolean
): string[] {
  if (!isRecord(map)) return []
  return Object.keys(map).filter((k) => {
    const obs = map[k]
    return isRecord(obs) && predicate(obs)
  })
}

function priceObservationKeysForField(
  prices: unknown,
  field: 'retailPrice' | 'salePrice' | 'finalPrice'
): string[] {
  return observationKeys(prices, (obs) => obs[field] != null)
}

function snapshotKeysForProductDataField(
  snapshots: unknown,
  field: 'retailPrice' | 'salePrice' | 'finalPrice'
): string[] {
  if (!isRecord(snapshots)) return []
  return Object.keys(snapshots).filter((k) => {
    const obs = snapshots[k]
    if (!isRecord(obs)) return false
    const pd = obs.productData
    if (!isRecord(pd)) return false
    return pd[field] != null
  })
}

function productIdKeys(productIds: unknown): string[] {
  return observationKeys(productIds, (obs) => {
    const id = obs.productId
    return typeof id === 'string' && id.trim().length > 0
  })
}

function promoCodeKeys(promoCodes: unknown): string[] {
  return observationKeys(promoCodes, (obs) => {
    const code = obs.code
    return typeof code === 'string' && code.trim().length > 0
  })
}

function couponKeys(coupons: unknown): string[] {
  return observationKeys(coupons, (obs) => {
    const ci = obs.couponInfo
    if (!isRecord(ci)) return false
    return ci.hasCoupon === true || (typeof ci.couponCode === 'string' && ci.couponCode.length > 0)
  })
}

function discountKeys(discounts: unknown): string[] {
  return observationKeys(discounts, (obs) => obs.value != null || obs.type != null)
}

/** Evidence map keys (e.g. `amazon.asinParse`, `postText.regexPrices`) contributing to each final-deal row. */
export interface FinalDealFieldEvidenceIds {
  productId: string[]
  retailPrice: string[]
  salePrice: string[]
  finalPrice: string[]
  discountPercent: string[]
  promoCode: string[]
  coupon: string[]
}

export function buildFinalDealFieldEvidenceIds(
  evidence: PostToDealEvidence | null | undefined
): FinalDealFieldEvidenceIds {
  if (!evidence) {
    return {
      productId: [],
      retailPrice: [],
      salePrice: [],
      finalPrice: [],
      discountPercent: [],
      promoCode: [],
      coupon: []
    }
  }

  const retailPrice = uniqueSorted([
    ...priceObservationKeysForField(evidence.prices, 'retailPrice'),
    ...snapshotKeysForProductDataField(evidence.productSnapshots, 'retailPrice')
  ])

  const salePrice = uniqueSorted([
    ...priceObservationKeysForField(evidence.prices, 'salePrice'),
    ...snapshotKeysForProductDataField(evidence.productSnapshots, 'salePrice')
  ])

  const finalPrice = uniqueSorted([
    ...priceObservationKeysForField(evidence.prices, 'finalPrice'),
    ...snapshotKeysForProductDataField(evidence.productSnapshots, 'finalPrice')
  ])

  return {
    productId: uniqueSorted(productIdKeys(evidence.productIds)),
    retailPrice,
    salePrice,
    finalPrice,
    discountPercent: uniqueSorted(discountKeys(evidence.discounts)),
    promoCode: uniqueSorted(promoCodeKeys(evidence.promoCodes)),
    coupon: uniqueSorted(couponKeys(evidence.coupons))
  }
}

export function formatEvidenceIdList(ids: string[]): string {
  if (!ids.length) return '—'
  return ids.join(', ')
}
